/**
 * Flex-Algo highlight engine for the topology graph.
 *
 * Produces three sets per `(algorithm, mode)` combination so the graph
 * can render a layered visualization:
 *
 *   - `nodeIds`         — nodes that have the algorithm provisioned
 *                         (any srv6Locator with `algorithm === selected`).
 *   - `trafficLinkIds`  — links currently carrying real traffic for the
 *                         algorithm, joined from SRv6 DDM demands
 *                         (`GET /srv6-insight/ddm?algo=`) against the
 *                         topology's `(source|target)Interface`.
 *   - `spfLinkIds`      — links the flex-algo SPF would prefer over
 *                         standard IGP (see `computeSpfHighlightEdges`).
 *
 * Anything outside the relevant union is dimmed by the renderer
 * (`TopologyGraph` → `RouterNode` / `LinkEdge`). When `mode === 'off'`
 * or `algorithm == null` we return empty sets and the renderer treats
 * the filter as inactive — no opacity changes anywhere.
 */

import {
    TopologyData,
    TopologyLink,
    AlgoLinkMode,
    LinksByAlgoResponse,
    toShortHostname,
} from '../TopologyData';
import { computeIsisSpt, metricFnForFlexAlgo, LinkMetricFn } from './pathfinder';

export interface AlgoHighlight {
    /** True when the filter is active and the renderer should dim non-matches. */
    active: boolean;
    /** Selected algorithm (echo-back, useful for tooltips/badges). */
    algorithm: number | null;
    /** Active link mode — drives which sets the renderer consumes. */
    mode: AlgoLinkMode;
    /** Nodes that have the algorithm in their srv6Locators. */
    nodeIds: Set<string>;
    /** Links carrying real traffic for the algorithm (mode 2A). */
    trafficLinkIds: Set<string>;
    /** Links the SPF would compute for the algorithm (mode 2B). */
    spfLinkIds: Set<string>;
    /** Union of traffic + spf — what counts as "highlighted" for dimming. */
    highlightedLinkIds: Set<string>;
}

const EMPTY: AlgoHighlight = {
    active: false,
    algorithm: null,
    mode: 'off',
    nodeIds: new Set(),
    trafficLinkIds: new Set(),
    spfLinkIds: new Set(),
    highlightedLinkIds: new Set(),
};

/** Normalize Cisco-style interface names for Influx ↔ ISIS joins. */
function normalizeInterfaceName(iface: string): string {
    return iface.trim().toLowerCase();
}

function routerLookupKey(host: string): string {
    return toShortHostname(host).toLowerCase();
}

function interfaceIndexKey(host: string, iface: string): string {
    return `${routerLookupKey(host)}|${normalizeInterfaceName(iface)}`;
}

/**
 * Build the `(short hostname, interface) → linkId` index used to join
 * the InfluxDB traffic rows back to the topology graph. We index BOTH
 * directions (forward and reverse) because `cef_srv6_locator_counters`
 * reports the egress interface from whichever side is sourcing the
 * locator — that side may be `link.source` OR `link.target` depending
 * on the locator's owner.
 */
function buildInterfaceIndex(
    data: TopologyData
): { fwd: Map<string, string>; rev: Map<string, string> } {
    const fwd = new Map<string, string>();
    const rev = new Map<string, string>();

    const register = (
        map: Map<string, string>,
        host: string,
        iface: string,
        linkId: string
    ) => {
        map.set(interfaceIndexKey(host, iface), linkId);
        // Also index the raw node id when it differs from the short form.
        const short = routerLookupKey(host);
        if (host.toLowerCase() !== short) {
            map.set(`${host.toLowerCase()}|${normalizeInterfaceName(iface)}`, linkId);
        }
    };

    for (const link of data.links) {
        if (link.source && link.sourceInterface) {
            register(fwd, link.source, link.sourceInterface, link.id);
        }
        if (link.target && link.targetInterface) {
            register(rev, link.target, link.targetInterface, link.id);
        }
    }
    return { fwd, rev };
}

/**
 * Resolve a traffic row `(sourceShort, interface)` to a topology link id.
 * Tries normalized short keys first, then walks nodes when the source
 * string is an FQDN-style id.
 */
function resolveTrafficLinkId(
    data: TopologyData,
    source: string,
    iface: string,
    index: { fwd: Map<string, string>; rev: Map<string, string> }
): string | undefined {
    const normIface = normalizeInterfaceName(iface);
    const sourceLower = source.trim().toLowerCase();

    const directKey = `${sourceLower}|${normIface}`;
    const fromDirect = index.fwd.get(directKey) ?? index.rev.get(directKey);
    if (fromDirect) return fromDirect;

    const shortKey = `${routerLookupKey(source)}|${normIface}`;
    const fromShort = index.fwd.get(shortKey) ?? index.rev.get(shortKey);
    if (fromShort) return fromShort;

    for (const node of data.nodes) {
        const matches =
            routerLookupKey(node.id) === sourceLower ||
            routerLookupKey(node.hostname) === sourceLower ||
            node.id.toLowerCase() === sourceLower;
        if (!matches) continue;

        const key = interfaceIndexKey(node.id, iface);
        const id = index.fwd.get(key) ?? index.rev.get(key);
        if (id) return id;
    }

    return undefined;
}

/** True when both routers on the link participate in the selected algo. */
function isIntraParticipantLink(
    link: TopologyLink,
    participantNodeIds: Set<string>
): boolean {
    return (
        participantNodeIds.has(link.source) &&
        participantNodeIds.has(link.target)
    );
}

function filterToParticipantSubgraph(
    data: TopologyData,
    linkIds: Set<string>,
    participantNodeIds: Set<string>
): Set<string> {
    const filtered = new Set<string>();
    for (const id of linkIds) {
        const link = data.links.find((l) => l.id === id);
        if (link && isIntraParticipantLink(link, participantNodeIds)) {
            filtered.add(id);
        }
    }
    return filtered;
}

/**
 * Resolve flex-algo `metricType` for the selected algorithm.
 * 0 = IGP, 1 = min-delay, 2 = TE (falls back to IGP in pathfinder).
 */
function resolveMetricType(
    data: TopologyData,
    algorithm: number,
    participantNodeIds: Set<string>
): 0 | 1 | 2 {
    for (const node of data.nodes) {
        if (!participantNodeIds.has(node.id)) continue;
        const def = node.flexAlgoDefinitions?.find(
            (d) => d.algorithm === algorithm
        );
        if (def) return def.metricType;
    }
    return 0;
}

function resolveMetricFn(
    data: TopologyData,
    algorithm: number,
    participantNodeIds: Set<string>
): LinkMetricFn {
    return metricFnForFlexAlgo(
        resolveMetricType(data, algorithm, participantNodeIds)
    );
}

/** Union of SPT tree edges rooted at every participating node. */
function unionSptEdgeIds(
    data: TopologyData,
    roots: Set<string>,
    metricFn: LinkMetricFn
): Set<string> {
    const union = new Set<string>();
    for (const root of roots) {
        const spt = computeIsisSpt(data.links, root, undefined, undefined, metricFn);
        for (const edgeId of spt.treeEdgeIds) union.add(edgeId);
    }
    return union;
}

/**
 * Links the flex-algo SPF would use — meaningful subset, not the full
 * graph union (which would highlight every edge in a connected network).
 *
 *  - min-delay / TE (`metricType !== 0`): edges on the flex-algo SPT
 *    that are NOT on the standard IGP SPT (routing differs by metric).
 *  - IGP flex-algo (`metricType === 0`): edges inside the participant
 *    subgraph only (both endpoints provisioned for the algo).
 */
function computeSpfHighlightEdges(
    data: TopologyData,
    algorithm: number,
    participantNodeIds: Set<string>
): Set<string> {
    if (participantNodeIds.size === 0) return new Set();

    const metricType = resolveMetricType(data, algorithm, participantNodeIds);
    const flexMetricFn = resolveMetricFn(data, algorithm, participantNodeIds);
    const igpMetricFn = metricFnForFlexAlgo(0);

    const flexUnion = unionSptEdgeIds(
        data,
        participantNodeIds,
        flexMetricFn
    );
    const participantOnly = filterToParticipantSubgraph(
        data,
        flexUnion,
        participantNodeIds
    );

    if (metricType === 0) {
        return participantOnly;
    }

    const igpUnion = unionSptEdgeIds(
        data,
        participantNodeIds,
        igpMetricFn
    );

    const diff = new Set<string>();
    for (const id of participantOnly) {
        if (!igpUnion.has(id)) diff.add(id);
    }

    // When flex and IGP agree on every tree edge, fall back to the
    // participant subgraph so SPF mode still shows something useful.
    return diff.size > 0 ? diff : participantOnly;
}

/**
 * Main entry point. Returns a stable `AlgoHighlight` shape regardless
 * of mode/algo — callers just key off `active` and the link-id sets.
 */
export function computeAlgoHighlight(
    data: TopologyData,
    algorithm: number | null,
    mode: AlgoLinkMode,
    trafficLinks: LinksByAlgoResponse | null
): AlgoHighlight {
    if (algorithm == null) return EMPTY;

    const nodeIds = new Set<string>();
    for (const node of data.nodes) {
        if (node.srv6Locators.some((l) => l.algorithm === algorithm)) {
            nodeIds.add(node.id);
        }
    }

    const trafficLinkIds = new Set<string>();
    if (
        (mode === 'traffic' || mode === 'both') &&
        trafficLinks &&
        trafficLinks.algorithm === algorithm
    ) {
        const index = buildInterfaceIndex(data);
        for (const tl of trafficLinks.links) {
            const id = resolveTrafficLinkId(
                data,
                tl.source,
                tl.interface,
                index
            );
            if (id) trafficLinkIds.add(id);
        }
    }

    const rawSpfLinkIds =
        mode === 'spf' || mode === 'both'
            ? computeSpfHighlightEdges(data, algorithm, nodeIds)
            : new Set<string>();

    const spfLinkIds = filterToParticipantSubgraph(
        data,
        rawSpfLinkIds,
        nodeIds
    );

    const highlightedLinkIds = new Set<string>([
        ...filterToParticipantSubgraph(data, trafficLinkIds, nodeIds),
        ...spfLinkIds,
    ]);

    return {
        active: true,
        algorithm,
        mode,
        nodeIds,
        trafficLinkIds: filterToParticipantSubgraph(
            data,
            trafficLinkIds,
            nodeIds
        ),
        spfLinkIds,
        highlightedLinkIds,
    };
}

/** Whether both endpoints of a link participate in the active algo filter. */
export function linkParticipatesInAlgoSubgraph(
    link: TopologyLink,
    highlight: AlgoHighlight | null | undefined
): boolean {
    if (!highlight?.active) return true;
    return isIntraParticipantLink(link, highlight.nodeIds);
}
